"use client"

import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "@/lib/utils"
import { Button, type ButtonVariant } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  useResolvedDialogPresentationMode,
} from "@/components/ui/dialog"

interface EmbeddedAlertDialogContextValue {
  embedded: boolean
  onOpenChange?: (open: boolean) => void
}

const EmbeddedAlertDialogContext = React.createContext<EmbeddedAlertDialogContextValue>({
  embedded: false,
})

function useEmbeddedAlertDialog() {
  return React.useContext(EmbeddedAlertDialogContext)
}

function AlertDialog({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  const presentationMode = useResolvedDialogPresentationMode()
  const embedded = presentationMode === "embedded"

  return (
    <EmbeddedAlertDialogContext.Provider value={{ embedded, onOpenChange: props.onOpenChange }}>
      {embedded ? (
        <Dialog open={props.open} defaultOpen={props.defaultOpen} onOpenChange={props.onOpenChange}>
          {props.children}
        </Dialog>
      ) : (
        <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
      )}
    </EmbeddedAlertDialogContext.Provider>
  )
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  const { embedded } = useEmbeddedAlertDialog()

  if (embedded) {
    return <DialogTrigger data-slot="alert-dialog-trigger" {...props} />
  }

  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  )
}

function AlertDialogPortal({
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  const { embedded } = useEmbeddedAlertDialog()

  if (embedded) {
    return <DialogPortal data-slot="alert-dialog-portal">{children}</DialogPortal>
  }

  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props}>
      {children}
    </AlertDialogPrimitive.Portal>
  )
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  const { embedded } = useEmbeddedAlertDialog()

  if (embedded) {
    return <DialogOverlay data-slot="alert-dialog-overlay" className={className} />
  }

  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 dark:bg-black/80 backdrop-blur-sm",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
  size?: "default" | "sm"
}) {
  const { embedded } = useEmbeddedAlertDialog()

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      {embedded ? (
        <DialogContent
          data-slot="alert-dialog-content"
          data-size={size}
          data-preview-embedded="true"
          showCloseButton={false}
          className={cn(
            "bg-background group/alert-dialog-content data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-lg",
            className,
            "!relative !inset-auto !left-auto !top-auto !z-auto !mx-auto !w-full !translate-x-0 !translate-y-0 !shadow-none"
          )}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            props.onOpenAutoFocus?.(event)
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            props.onCloseAutoFocus?.(event)
          }}
          {...props}
        />
      ) : (
        <AlertDialogPrimitive.Content
          data-slot="alert-dialog-content"
          data-size={size}
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 group/alert-dialog-content fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-lg",
            className
          )}
          {...props}
        />
      )}
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        "grid grid-rows-[auto_1fr] place-items-center gap-1.5 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-6 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  const { embedded } = useEmbeddedAlertDialog()

  if (embedded) {
    return (
      <DialogTitle
        data-slot="alert-dialog-title"
        className={cn(
          "text-lg font-semibold sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
          className
        )}
        {...props}
      />
    )
  }

  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        "text-lg font-semibold sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  const { embedded } = useEmbeddedAlertDialog()

  if (embedded) {
    return (
      <DialogDescription
        data-slot="alert-dialog-description"
        className={cn("text-muted-foreground text-sm", className)}
        {...props}
      />
    )
  }

  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function AlertDialogMedia({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-media"
      className={cn(
        "bg-muted mb-2 inline-flex size-16 items-center justify-center rounded-md sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-8",
        className
      )}
      {...props}
    />
  )
}

type AlertDialogActionVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'solid' | 'dash' | 'soft';
type AlertDialogActionSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const alertDialogButtonVariantMap = {
  primary: { variant: 'primary' },
  secondary: { variant: 'secondary' },
  outline: { variant: 'outline' },
  ghost: { variant: 'ghost' },
  danger: { variant: 'danger' },
  neutral: { variant: 'neutral' },
  error: { variant: 'error' },
  warning: { variant: 'warning' },
  success: { variant: 'success' },
  info: { variant: 'info' },
  solid: { variant: 'primary' },
  dash: { variant: 'neutral-dash' },
  soft: { variant: 'soft' },
} satisfies Record<AlertDialogActionVariant, { variant: ButtonVariant | 'primary' | 'secondary' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'neutral-dash' }>

function AlertDialogAction({
  className,
  variant = "primary",
  size = "sm",
  color,
  onClick,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> & {
  color?: string;
  variant?: AlertDialogActionVariant;
  size?: AlertDialogActionSize;
}) {
  const { embedded, onOpenChange } = useEmbeddedAlertDialog()
  void color
  const buttonProps = alertDialogButtonVariantMap[variant] || alertDialogButtonVariantMap.primary

  if (embedded) {
    return (
      <Button
        data-slot="alert-dialog-action"
        size={size}
        variant={buttonProps.variant as ButtonVariant | 'primary' | 'secondary' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'neutral-dash'}
        className={className}
        onClick={(event) => {
          onClick?.(event)
          if (!event.defaultPrevented) {
            onOpenChange?.(false)
          }
        }}
        {...props}
      />
    )
  }

  return (
    <AlertDialogPrimitive.Action asChild>
      <Button
        data-slot="alert-dialog-action"
        size={size}
        variant={buttonProps.variant as ButtonVariant | 'primary' | 'secondary' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'neutral-dash'}
        className={className}
        onClick={onClick}
        {...props}
      />
    </AlertDialogPrimitive.Action>
  )
}

function AlertDialogCancel({
  className,
  variant = "outline",
  size = "sm",
  color,
  onClick,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> & {
  color?: string;
  variant?: AlertDialogActionVariant;
  size?: AlertDialogActionSize;
}) {
  const { embedded, onOpenChange } = useEmbeddedAlertDialog()
  void color
  const buttonProps = alertDialogButtonVariantMap[variant] || alertDialogButtonVariantMap.outline

  if (embedded) {
    return (
      <Button
        data-slot="alert-dialog-cancel"
        size={size}
        variant={buttonProps.variant as ButtonVariant | 'primary' | 'secondary' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'neutral-dash'}
        className={className}
        onClick={(event) => {
          onClick?.(event)
          if (!event.defaultPrevented) {
            onOpenChange?.(false)
          }
        }}
        {...props}
      />
    )
  }

  return (
    <AlertDialogPrimitive.Cancel asChild>
      <Button
        data-slot="alert-dialog-cancel"
        size={size}
        variant={buttonProps.variant as ButtonVariant | 'primary' | 'secondary' | 'danger' | 'neutral' | 'error' | 'warning' | 'success' | 'info' | 'neutral-dash'}
        className={className}
        onClick={onClick}
        {...props}
      />
    </AlertDialogPrimitive.Cancel>
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
